import { useState } from "react";

import { LoadingSpinner } from "@/components/loading";
import { useProgressNavigation } from "@/components/progress-bar";
import { Tooltip } from "@/components/ui";

import { queryClient } from "@/config/react-query";

import { useCommunication } from "@/contexts/chat-provider";
import { useIsMobile } from "@/contexts/is-mobile-provider";
import { useUser } from "@/contexts/user-provider";

import { formatPhoneNumberOrHidden } from "@/lib/format-data";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { clickableText, cx } from "@/lib/tailwind-utils";

import { UserTiny } from "@/types/user";

import { chatQueryOptions } from "@/query/comms/chat";

interface ClickablePhoneNumberProps {
  patient: Pick<UserTiny, "id" | "phoneNumber" | "hasPhoneNumber">;
  className?: string;
  onClick?: () => void;
}
export default function ClickablePhoneNumber({
  patient,
  className,
  onClick,
}: ClickablePhoneNumberProps) {
  const { openChat } = useCommunication();
  const { currentUser } = useUser();
  const { isMobile } = useIsMobile();
  const [isLoading, setIsLoading] = useState(false);
  const { navigateWithProgress } = useProgressNavigation();

  const canViewMessages = hasPermission(
    currentUser,
    PERMISSIONS.MESSAGING_CHATS_READ,
  );

  // If user doesn't have permission to view messages, show as plain text
  if (!canViewMessages) {
    return (
      <Tooltip
        content="You have no permissions to view messages"
        triggerAsChild
        side="top"
        sideOffset={10}
      >
        <span
          className={cx("font-semibold text-foreground-secondary", className)}
        >
          {formatPhoneNumberOrHidden(patient)}
        </span>
      </Tooltip>
    );
  }

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    // On mobile, redirect to chat page
    if (isMobile) {
      setIsLoading(true);
      try {
        const chat = await queryClient.fetchQuery(
          chatQueryOptions.fromPatient(patient.id),
        );
        if (chat?.id) {
          navigateWithProgress(`/comms/chat?chatId=${chat.id}`);
        }
        onClick?.();
      } catch (error) {
        console.error("Failed to get chat ID:", error);
      } finally {
        setIsLoading(false);
      }
    } else {
      openChat(patient.id);
      onClick?.();
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className={cx(clickableText, className, isLoading && "opacity-50")}
    >
      {isLoading ? (
        <span className="flex items-center gap-2">
          <LoadingSpinner size={16} />
          {formatPhoneNumberOrHidden(patient)}
        </span>
      ) : (
        formatPhoneNumberOrHidden(patient)
      )}
    </button>
  );
}
